// @ts-nocheck
//
// Open Order -> Order Tracking Center field sync.
//
// Two fields ride this path, and they behave differently:
//
//   Tracking Number  - per BLOCK on Open Order (3 blocks, 3 items each) but per
//                      SLOT on Order Tracking (5 flat slots). Block index and
//                      slot index are not the same thing, so the model number
//                      is the bridge: find the slot whose model matches one of
//                      the block's models and write the tracking number there.
//
//   Customer PO      - record-level on both sides (open_order.cust_po ->
//                      order_tracking.po_number, the "Cust PO #" field), so it
//                      is written to every Order Tracking row for the quote.
//
// Everything here is silent by design — Open Order autosaves without any
// feedback, so a failed or unmatched sync only ever reaches the console.

import { getOrderTrackingByQuoteId, updateOrderTracking } from "./API";
import { getRowId, otMarkDirty, otUpsertRecord } from "./orderTrackingCache";

// Order Tracking's 5 slots, each mapping to its server column names.
const OT_SLOTS = [
  { model: "model", manu: "manu", vendor: "vendor", tracking: "tracking_no", serial: "serial_num1" },
  { model: "model_2", manu: "manu2", vendor: "vendor2", tracking: "tracking_no_2", serial: "serial_num2" },
  { model: "model_3", manu: "manu3", vendor: "vendor3", tracking: "tracking_no_3", serial: "serial_num3" },
  { model: "model_4", manu: "manu4", vendor: "vendor4", tracking: "tracking_no_4", serial: "serial_num4" },
  { model: "model_5", manu: "manu5", vendor: "vendor5", tracking: "tracking_no_5", serial: "serial_num5" },
];

const norm = (value: any) =>
  String(value ?? "")
    .trim()
    .toUpperCase();

// Ranked candidate for "which record+slot does this block's tracking belong to".
// Lower rank wins: 0 = model+manu match, 1 = model match, 2 = positional
// fallback onto an empty slot.
const collectCandidates = (records: any[], block: any, blockIndex: number) => {
  const models = new Set(
    (block?.items || []).map((i: any) => norm(i?.model)).filter(Boolean),
  );
  const manus = new Set(
    (block?.items || []).map((i: any) => norm(i?.manu)).filter(Boolean),
  );
  const vendor = norm(block?.vendor);

  const candidates: any[] = [];

  records.forEach((record, recordIndex) => {
    OT_SLOTS.forEach((slot, slotIndex) => {
      const slotModel = norm(record?.[slot.model]);
      const slotTracking = norm(record?.[slot.tracking]);

      if (slotModel && models.has(slotModel)) {
        const slotManu = norm(record?.[slot.manu]);
        const slotVendor = norm(record?.[slot.vendor]);
        const manuMatch = slotManu && manus.has(slotManu);
        const vendorMatch = vendor && slotVendor === vendor;
        candidates.push({
          record,
          recordIndex,
          slotIndex,
          slot,
          rank: manuMatch || vendorMatch ? 0 : 1,
          // Prefer a still-empty slot when two slots are otherwise equal, so a
          // duplicated model fills the blank one instead of overwriting.
          emptyFirst: slotTracking ? 1 : 0,
        });
      } else if (!slotModel && slotIndex === blockIndex) {
        // No model on either side to match on — a bare row can still take the
        // positional slot. Never lands on a slot that holds a different model.
        candidates.push({
          record,
          recordIndex,
          slotIndex,
          slot,
          rank: 2,
          emptyFirst: slotTracking ? 1 : 0,
        });
      }
    });
  });

  // records arrive date DESC from the API, so a lower recordIndex is newer.
  candidates.sort(
    (a, b) =>
      a.rank - b.rank ||
      a.emptyFirst - b.emptyFirst ||
      a.recordIndex - b.recordIndex ||
      a.slotIndex - b.slotIndex,
  );

  return candidates;
};

// Queue one row's patch, applying it to the in-memory copy so a later block in
// the same pass sees it, and coalescing several fields onto the same row into a
// single PUT.
const stagePatch = (pending: Map<any, any>, record: any, patch: any) => {
  Object.assign(record, patch);
  pending.set(record, { ...(pending.get(record) || {}), ...patch });
};

// Find the slot on one Order Tracking row that holds this Open Order item:
// model+manu match first, then model only, then the item's flat position
// (block*3 + item) when neither side has a model. Never lands on a slot that
// holds a different model.
const findSerialSlot = (
  record: any,
  item: any,
  flatIndex: number,
  claimed: Set<number>,
) => {
  const model = norm(item?.model);
  const manu = norm(item?.manu);
  let best: { slotIndex: number; rank: number } | null = null;
  OT_SLOTS.forEach((slot, slotIndex) => {
    if (claimed.has(slotIndex)) return;
    const slotModel = norm(record?.[slot.model]);
    let rank = -1;
    if (model && slotModel === model) {
      rank = manu && norm(record?.[slot.manu]) === manu ? 0 : 1;
    } else if (!model && !slotModel && slotIndex === flatIndex) {
      rank = 2;
    }
    if (rank < 0) return;
    // Tie-break on the item's own position so duplicate models keep order.
    if (
      !best ||
      rank < best.rank ||
      (rank === best.rank && slotIndex === flatIndex)
    ) {
      best = { slotIndex, rank };
    }
  });
  return best ? best.slotIndex : -1;
};

// Sync the given Open Order fields into the matching Order Tracking rows.
// `blockIndexes` are the blocks whose tracking number changed; `syncCustPo`
// pushes the record's Customer PO onto every row for the quote; `serialItems`
// ("block:item" keys) are the line items whose serial changed, written to the
// model-matched slot on every row for the quote. Resolves once every write
// has been attempted; never throws and never surfaces UI feedback.
export const syncOpenOrderToOrderTracking = async (
  order: any,
  {
    blockIndexes = [],
    syncCustPo = false,
    serialItems = [],
  }: { blockIndexes?: number[]; syncCustPo?: boolean; serialItems?: string[] } = {},
) => {
  const quoteId = String(order?.quoteId || "").trim();
  if (!quoteId || (!blockIndexes.length && !syncCustPo && !serialItems.length)) return;

  let records: any[] = [];
  try {
    const response = await getOrderTrackingByQuoteId(quoteId);
    records = Array.isArray(response) ? response : response ? [response] : [];
  } catch (error) {
    console.warn("[openOrderTrackingSync] lookup failed:", error);
    return;
  }
  if (!records.length) return;

  // Row -> merged patch, so a row touched by both the Cust PO and a tracking
  // number is written once.
  const pending = new Map<any, any>();

  // The Customer PO is the same value for the whole quote, so every row gets it.
  if (syncCustPo) {
    const custPo = String(order?.custPo || "").trim();
    records.forEach((record) => {
      if (String(record?.po_number || "").trim() === custPo) return;
      stagePatch(pending, record, { po_number: custPo || null });
    });
  }

  // Two blocks synced in the same pass must not land on the same slot.
  const claimed = new Set<string>();

  for (const blockIndex of blockIndexes) {
    const block = order?.orderBlocks?.[blockIndex];
    if (!block) continue;

    const value = String(block.tracking || "").trim();
    const target = collectCandidates(records, block, blockIndex).find(
      (c) => !claimed.has(`${c.recordIndex}:${c.slotIndex}`),
    );
    if (!target) continue;

    const id = getRowId(target.record);
    if (id == null) continue;

    const existing = String(target.record?.[target.slot.tracking] || "").trim();
    if (existing === value) {
      claimed.add(`${target.recordIndex}:${target.slotIndex}`);
      continue;
    }

    claimed.add(`${target.recordIndex}:${target.slotIndex}`);
    stagePatch(pending, target.record, { [target.slot.tracking]: value || null });
  }

  // Serials: every row for the quote carries the same line items, so each
  // changed item's serial goes to its model-matched slot on each row.
  if (serialItems.length) {
    records.forEach((record) => {
      const taken = new Set<number>();
      for (const itemKey of serialItems) {
        const [b, i] = itemKey.split(":").map(Number);
        const item = order?.orderBlocks?.[b]?.items?.[i];
        if (!item) continue;
        const slotIndex = findSerialSlot(record, item, b * 3 + i, taken);
        if (slotIndex < 0) continue;
        taken.add(slotIndex);
        const key = OT_SLOTS[slotIndex].serial;
        const value = String(item.serial || "").trim();
        if (String(record?.[key] || "").trim() === value) continue;
        stagePatch(pending, record, { [key]: value || null });
      }
    });
  }

  for (const [record, patch] of pending) {
    const id = getRowId(record);
    if (id == null) continue;
    try {
      await updateOrderTracking(id, patch);
      otUpsertRecord(id, patch);
    } catch (error) {
      // Mirror locally anyway and let the existing dirty-queue flush retry.
      otUpsertRecord(id, patch);
      otMarkDirty(id, patch);
      console.warn("[openOrderTrackingSync] update failed:", error);
    }
  }
};
